import type { t_remote_type, t_scaffold_type, IRunPlan, IRemoveViewCrate, IRemoveViewMicro, IScaffold } from '../types';
import type { CrateRemoteView } from '../remote_views/crate';
import type { MicroRemoteView } from '../remote_views/micro';
import type { RemoteView } from '../remote_views/remote_view';

type TBodyPortalArtifacts = HTMLElement & {
  __portal: {
    artifacts: Record<string, { shutdown: () => Promise<void> }>;
  };
};

type TLifeCycleStage = (runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) => Promise<void>;
type TLifeCycleStages = Record<t_scaffold_type, Record<t_remote_type, TLifeCycleStage>>;
type t_life_cycle_stage = 'startup' | 'shutdown';

interface ILifeCycleBridge {
  stages: {
    startup: TLifeCycleStages;
    shutdown: TLifeCycleStages;
  };
}

/**
 * handles the life-cycle methods for remote_view x scaffold
 */
export class LifeCycleBridge implements ILifeCycleBridge {
  readonly stages = {
    startup: {
      iframe: {
        async crate(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // not needed yet
        },
        async micro(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // not needed yet
        },
        async hosting(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // not needed yet
        }
      },
      region: {
        async crate(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // not needed yet
        },
        async micro(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // not needed yet
        },
        async hosting(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // not needed yet
        }
      }
    },
    shutdown: {
      iframe: {
        async crate(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // do nothing, because iframe destroys all data and DOM
        },
        async micro(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // do nothing, because iframe destroys all data and DOM
        },
        async hosting(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // do nothing, because iframe destroys all data and DOM
        }
      },
      region: {
        async crate(runPlan: IRunPlan<IRemoveViewCrate>, remoteView: CrateRemoteView, scaffold: IScaffold) {
          if (!('$ip' in globalThis)) {
            return;
          }

          const $ip = globalThis.$ip;

          (runPlan.remote_view.crates ?? []).forEach((crate) =>
            $ip.$loader.unloadCrate(crate.id, crate.version, crate.settings ?? {})
          );
        },
        async micro(runPlan: IRunPlan<IRemoveViewMicro>, remoteView: MicroRemoteView, scaffold: IScaffold) {
          const microURI = runPlan.remote_view.uri;
          const indexFile = runPlan.remote_view.index_file;

          const portalShared =
            (document.body as TBodyPortalArtifacts).__portal ?? ({} as TBodyPortalArtifacts['__portal']);
          const portalArtifacts = portalShared.artifacts;
          if (!portalArtifacts) {
            return;
          }

          const micro = portalArtifacts[runPlan.remote_view.uri];
          if (!micro) {
            return;
          }

          // shutdown the micro so it stops watching the uri
          //
          await micro.shutdown();

          const $ip = globalThis.$ip;
          if (!$ip) {
            return;
          }

          await $ip.unrequires([`${microURI}/${indexFile}`, `${microURI}/index.css`]);

          // remove the micro from document.body so it can be garbage collected
          //
          delete portalArtifacts[runPlan.remote_view.uri];
        },
        async hosting(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
          // hosting is not allowed to run in a Region IScaffold
        }
      }
    }
  };

  async startup(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
    return this._lifeCycle('startup', runPlan, remoteView, scaffold);
  }

  async shutdown(runPlan: IRunPlan, remoteView: RemoteView, scaffold: IScaffold) {
    return this._lifeCycle('shutdown', runPlan, remoteView, scaffold);
  }

  protected async _lifeCycle(
    stage: t_life_cycle_stage,
    runPlan: IRunPlan,
    remoteView: RemoteView,
    scaffold: IScaffold
  ) {
    const scaffoldType = runPlan.scaffold.type;
    const remoteViewType = runPlan.remote_view.type;
    const lifeCycleStage = this.stages[stage][scaffoldType][remoteViewType] as TLifeCycleStage;

    return lifeCycleStage(runPlan, remoteView, scaffold);
  }
}
